/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Text, BrandColors } from '@blend/enterprise-components';
import { defaultModalWidth, modalContentPadding } from 'blb-styles/constants';

const defaultMaxHeight = 662;

const hoverStyles = {
  ':hover': {
    backgroundColor: BrandColors.CLOUD_30,
  },
};

const styles = StyleSheet.create({
  additionalResult: {
    padding: 8,
  },
  result: {
    ':focus': {
      backgroundColor: BrandColors.CLOUD_30,
    },
    ':nth-child(odd)': {
      ...hoverStyles,
      backgroundColor: BrandColors.CLOUD_10,
    },

    ...hoverStyles,

    cursor: 'pointer',
    display: 'flex',
  },
  resultBorder: {
    borderBottom: `1px solid ${BrandColors.CLOUD_30}`,
  },
  results: {
    backgroundColor: BrandColors.WHITE,
    border: `1px solid ${BrandColors.CLOUD_50}`,
    boxShadow: `0 2px 2px 1px ${BrandColors.CLOUD_50}`,
    marginTop: 8,
    overflowY: 'auto',
    zIndex: 2,
  },
  resultsInModal: {
    position: 'fixed',
    width: defaultModalWidth - 2 * modalContentPadding, // this sucks
  },
  resultsOutModal: {
    left: 0,
    position: 'absolute',
    right: 0,
    width: 'inherit',
  },
});

export namespace TypeaheadResultComponent {
  export interface Props {
    result: Record<string, string>;
  }
}

export namespace TypeaheadResults {
  export interface Props {
    ResultComponent: React.FC<TypeaheadResultComponent.Props>;
    hasExtendedResults?: boolean;
    isInModal?: boolean; // flag required due to how modals are set up unfortunately
    isInputFocused: boolean;
    maxResultsHeight?: number;
    name: string;
    onResultClick?: (result: any) => void;
    resultLimit?: number;
    results: any[];
  }
}

export const TypeaheadResults: React.FC<TypeaheadResults.Props> = ({
  isInputFocused,
  isInModal,
  maxResultsHeight,
  name,
  onResultClick,
  ResultComponent,
  results,
  resultLimit = 10,
}) => {
  if (results.length === 0 || !isInputFocused) return null;

  const modalSpecificClass = css(isInModal ? styles.resultsInModal : styles.resultsOutModal);

  return (
    <ul
      className={`${css(styles.results)} ${modalSpecificClass}`}
      id={`${name}-search-results`}
      style={{ maxHeight: maxResultsHeight || defaultMaxHeight }}
    >
      {results.slice(0, 10).map((result: TypeaheadResultComponent.Props['result'], index: number) => (
        <li
          className={`${css(styles.result, index < resultLimit && styles.resultBorder)}`}
          id={`it-typeahead-result-${index}`}
          key={`${name}-result-${index}`}
          onClick={() => onResultClick && onResultClick(result)}
          tabIndex={0}
        >
          {/* ResultComponent should either be a button or a link */}
          <ResultComponent result={result} />
        </li>
      ))}
      {results.length > resultLimit && (
        <li className={`${css(styles.result)} ${css(styles.additionalResult)}`} key={`it-${name}Search-MoreResults`}>
          <Text displayBlock={true} weight="semibold">
            Additional Results not displayed
          </Text>
          <Text displayBlock={true}>Please refine your search</Text>
        </li>
      )}
    </ul>
  );
};
